<template>
    <div class="dv2-section">
        <SectionHeader icon="shopping_cart" title="Pedidos/Compras" anchor="sec-pedidos" />

        <div class="dv2-grid dv2-grid--kpi-chart">
            <KpiCard :columns="2" :loading="loading">
                <KpiTile
                    v-for="tile in ordersKpis"
                    :key="tile.label"
                    v-bind="tile"
                />
            </KpiCard>

            <HighchartsCard
                title="Pedidos por mês — valor, economia gerada e quantidade"
                :tooltip="tooltips.orders"
                :loading="loading"
                :options="ordersOptions"
            />
        </div>

        <div class="dv2-grid dv2-grid--full">
            <TableCard
                title="Ranking de pedidos"
                :tooltip="tooltips.ordersRanking"
                :loading="loading"
            >
                <a-table
                    :columns="columns"
                    :data-source="ordersRanking"
                    :pagination="false"
                    :row-key="(row) => row.id"
                    size="small"
                />
            </TableCard>
        </div>
    </div>
</template>

<script>
import SectionHeader from "./SectionHeader.vue";
import KpiCard from "./KpiCard.vue";
import KpiTile from "./KpiTile.vue";
import HighchartsCard from "./HighchartsCard.vue";
import TableCard from "./TableCard.vue";
import DashboardV2Service from "@/services/v3/dashboard/DashboardV2Service";
import {
    baseTheme,
    xAxisDefaults,
    yAxisDefaults,
    pluck,
} from "../shared/highchartsTheme";
import { BRAND } from "../shared/constants";
import { brl, brlThousand, pctBR, formatMonthLabel } from "../shared/format";
import { buildKpiTiles, decimalBR } from "../shared/kpi";

const service = new DashboardV2Service();

const TOOLTIPS = {
    orders: "Colunas empilhadas: valor dos pedidos + economia gerada (soma = valor cotado). Linhas: pedidos emitidos e economia em %.",
    ordersRanking:
        "Pedidos ordenados pelo valor comprado, com fornecedor vencedor, comprador responsável e economia gerada.",
};

const ORDERS_STYLE = {
    ordersGenerated: {
        label: "Pedidos gerados",
        tooltip: "Pedidos de compra emitidos a partir das cotações do período.",
        format: "int",
        valueColor: BRAND.laranja,
        captionColor: BRAND.laranja,
        captionTemplate: (raw) =>
            `${decimalBR(raw.perClosedQuote, 1)} por cotação fechada`,
    },
    ordersValue: {
        label: "Valor dos pedidos",
        tooltip: "Soma do valor dos pedidos emitidos no período.",
        format: "currencyMi",
        valueColor: BRAND.azul,
        deltaMonthFormat: "currencyMil",
        deltaYearFormat: "currencyMi",
    },
    avgTicket: {
        label: "Ticket médio",
        tooltip:
            "Valor médio por pedido: valor dos pedidos ÷ total de pedidos.",
        format: "currency",
        valueColor: BRAND.preto,
        captionColor: BRAND.muted,
        captionTemplate: (raw) => {
            const sign = raw.itemsPerOrderDeltaMonth >= 0 ? "+" : "-";
            return `${decimalBR(
                raw.itemsPerOrder,
                1
            )} itens por pedido · mês ${sign}${decimalBR(
                Math.abs(raw.itemsPerOrderDeltaMonth),
                2
            )}`;
        },
    },
    savingsGenerated: {
        label: "Economia gerada",
        tooltip:
            "Diferença entre o valor cotado e o valor efetivamente pedido nas cotações do período.",
        format: "currencyMil",
        valueColor: BRAND.verde,
        captionColor: BRAND.verde,
        deltaMonthFormat: "currency",
        deltaYearFormat: "currency",
        captionTemplate: (raw) =>
            `${pctBR(raw.pctOfLastCost)} sobre último custo`,
    },
};

export default {
    name: "OrdersSection",

    components: {
        SectionHeader,
        KpiCard,
        KpiTile,
        HighchartsCard,
        TableCard,
    },

    props: {
        filters: { type: Object, default: () => ({}) },
    },

    data() {
        return {
            loading: true,
            tooltips: TOOLTIPS,
            ordersKpis: [],
            ordersChart: null,
            ordersRanking: [],
        };
    },

    computed: {
        ordersOptions() {
            if (!this.ordersChart) return {};
            const rows = this.ordersChart;
            const quoted = pluck(rows, "quoted");
            const approved = pluck(rows, "approved");
            const ordersIssued = pluck(rows, "ordersIssued");
            const savingsPct = pluck(rows, "savingsPct");
            const savingsGenerated = quoted.map((v, i) => v - approved[i]);
            const theme = baseTheme();

            return {
                ...theme,
                xAxis: xAxisDefaults({
                    categories: pluck(rows, "month").map(formatMonthLabel),
                    crosshair: true,
                }),
                yAxis: [
                    yAxisDefaults({
                        title: { text: "Valor (R$)" },
                        labels: {
                            style: { fontSize: "11px", color: BRAND.cinza },
                            formatter() {
                                return brlThousand(this.value);
                            },
                        },
                    }),
                    yAxisDefaults({
                        opposite: true,
                        gridLineWidth: 0,
                        title: { text: "Pedidos" },
                    }),
                    yAxisDefaults({
                        opposite: true,
                        gridLineWidth: 0,
                        max: 25,
                        title: { text: null },
                        labels: { enabled: false },
                    }),
                ],
                tooltip: { ...theme.tooltip, shared: true },
                plotOptions: {
                    ...theme.plotOptions,
                    column: {
                        borderRadius: 2,
                        stacking: "normal",
                        groupPadding: 0.08,
                    },
                },
                series: [
                    {
                        type: "column",
                        name: "Valor dos pedidos",
                        data: approved,
                        color: BRAND.azul,
                        stack: "valor",
                        tooltip: {
                            pointFormatter() {
                                return `<span style="color:${
                                    this.color
                                }">●</span> Valor dos pedidos: <b>${brl(
                                    this.y
                                )}</b><br/>`;
                            },
                        },
                    },
                    {
                        type: "column",
                        name: "Economia gerada",
                        data: savingsGenerated,
                        color: BRAND.verde,
                        stack: "valor",
                        stacking: "normal",
                        tooltip: {
                            pointFormatter() {
                                return `<span style="color:${
                                    this.color
                                }">●</span> Economia: <b>${brl(
                                    this.y
                                )}</b><br/>`;
                            },
                        },
                    },
                    {
                        type: "spline",
                        name: "Pedidos emitidos",
                        data: ordersIssued,
                        yAxis: 1,
                        color: BRAND.laranja,
                        lineWidth: 3,
                        marker: {
                            fillColor: "#fff",
                            lineColor: BRAND.laranja,
                            lineWidth: 2,
                            radius: 4,
                        },
                    },
                    {
                        type: "spline",
                        name: "Economia %",
                        data: savingsPct,
                        yAxis: 2,
                        color: BRAND.preto,
                        lineWidth: 2,
                        dashStyle: "ShortDash",
                        marker: {
                            fillColor: "#fff",
                            lineColor: BRAND.preto,
                            lineWidth: 2,
                            radius: 3,
                        },
                        tooltip: { valueSuffix: "%" },
                    },
                ],
            };
        },

        columns() {
            const h = this.$createElement;
            const statusColor = (status) => {
                if (status === "Emitido") return "green";
                if (status === "Aprovado") return "blue";
                return "gold";
            };

            return [
                {
                    title: "#",
                    align: "center",
                    width: 44,
                    customRender: (_, __, index) =>
                        h(
                            "span",
                            { style: { fontWeight: 700, color: BRAND.cinza } },
                            index + 1
                        ),
                },
                {
                    title: "Pedido",
                    dataIndex: "id",
                    customRender: (text) =>
                        h("span", { style: { fontWeight: 700 } }, text),
                },
                { title: "Cotação", dataIndex: "quoteId" },
                { title: "Fornecedor", dataIndex: "supplier" },
                { title: "Comprador", dataIndex: "buyer" },
                { title: "Itens", dataIndex: "items", align: "right" },
                {
                    title: "Valor",
                    dataIndex: "value",
                    align: "right",
                    sorter: (a, b) => a.value - b.value,
                    defaultSortOrder: "descend",
                    customRender: (v) => brl(v),
                },
                {
                    title: "Economia",
                    dataIndex: "savings",
                    align: "right",
                    customRender: (v, row) =>
                        h(
                            "span",
                            { style: { color: BRAND.verde, fontWeight: 700 } },
                            `${brl(v)}  ${pctBR(row.pct)}`
                        ),
                },
                { title: "Emitido em", dataIndex: "issuedAt", align: "right" },
                {
                    title: "Status",
                    dataIndex: "status",
                    align: "right",
                    customRender: (status) =>
                        h(
                            "a-tag",
                            { props: { color: statusColor(status) } },
                            status
                        ),
                },
            ];
        },
    },

    watch: {
        filters: {
            deep: true,
            handler() {
                this.fetch();
            },
        },
    },

    created() {
        this.fetch();
    },

    methods: {
        fetch() {
            this.loading = true;
            service.getPedidos(this.filters).then(({ data }) => {
                this.ordersKpis = buildKpiTiles(data.kpis, ORDERS_STYLE, {
                    valueFontSize: "34px",
                });
                this.ordersChart = data.historic;
                this.ordersRanking = [...data.ranking].sort(
                    (a, b) => b.value - a.value
                );
                this.loading = false;
            });
        },
    },
};
</script>
